import { useEffect, type FC } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import InspirationCard from 'src/presentation/components/organisms/InspirationCard';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useContentStore } from 'src/domain/stores/useContentStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const TutorialScreen: FC = () => {
	const tabBarInset = useTabBarInset();

	const { language } = useAuthStore();
	const { tutorial, tutorialLoading, getApiTutorial } = useContentStore();
	const { captureEvent } = useServicesStore();

	useEffect(() => {
		getApiTutorial().catch((error: ApiRequestError) =>
			captureEvent({ message: 'getApiTutorial failed', extra: { error } }),
		);
	}, [language, getApiTutorial, captureEvent]);

	return (
		<View style={styles.container}>
			<BackgroundPattern />
			<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }} style={Styles.screen.fullWidth}>
				<View style={styles.contentContainer}>
					<Text style={styles.title}>{Translations.tutorialsTitle}</Text>
					<Text style={styles.description}>{Translations.tutorialsDescription}</Text>

					{tutorialLoading ? (
						<LoadingIndicator style={Styles.loadingIndicator.belowHeader} />
					) : (
						tutorial?.data.map((item) => (
							<InspirationCard
								contentUrl={item.url}
								imageUrl={item.media}
								key={item.id}
								label={item.label}
								title={item.title}
							/>
						))
					)}
				</View>
				<View style={{ height: Dimens.size18 }} />
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: Dimens.flex1,
	},
	contentContainer: {
		flex: Dimens.flex1,
		paddingVertical: Dimens.padding18,
		paddingHorizontal: Dimens.padding18,
		gap: Dimens.gap18,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize24,
		lineHeight: Dimens.lineHeight32,
		color: Colors.gray800,
	},
	description: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize16,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray800,
		marginBottom: Dimens.padding8,
	},
});

export default TutorialScreen;
